import type { FC, RefObject } from 'react';
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import cn from 'classnames';

import { defaultHandler } from './components/autocomplete/config';
import type { TPosition } from './components/autocomplete/models';
import type { TSelect } from './models';
import { getDropdownPosition } from './helpers';

export const BaseSelect: FC<TSelect> = ({
    ArrowIcon,
    Autocomplete,
    Input,
    addHiddenInput = false,
    appendCurrent,
    arrow = false,
    autoComplete = 'off',
    autoSuggest,
    caption = '',
    className,
    disabled = false,
    error = false,
    forwardedRef,
    name = '',
    onBlur = defaultHandler,
    onCancel,
    onChange,
    onClick = defaultHandler,
    onFocus = defaultHandler,
    onSelect = defaultHandler,
    options = [],
    placeholder = '',
    readOnly = false,
    styles,
    success = false,
    value: selectValue = '',
    hideStatusIcon,
    SelectActions,
    dataTestId,
    ...rest
}) => {
    const inputRefFallback = useRef<HTMLInputElement>(null);
    const inputRef = (forwardedRef || inputRefFallback) as RefObject<HTMLInputElement>;
    const autocompleteRef = useRef<HTMLUListElement>(null);

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [currentItem, setCurrentItem] = useState(-1);
    const [currentPosition, setCurrentPosition] = useState<TPosition>('bottom');
    const [tempValue, setTempValue] = useState<string>('');
    const displayValue = `${selectValue}${tempValue}`;

    const refs = { autocompleteRef, inputRef };
    const state = { setTempValue, setIsOpen, setCurrentItem };
    const cb = { onBlur, onChange, onSelect, onFocus, onClick };
    const values = {
        name,
        selectValue: String(selectValue),
        displayValue,
        options,
        isOpen,
        currentItem,
        readOnly,
    };

    const actions = new SelectActions({ refs, state, cb, values });

    useEffect(() => {
        if (!autoSuggest) return;
        actions.insertSuggestion();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentItem, inputRef, isOpen, options, selectValue]);

    // При обновлении опций после закрытия актуализировать текущий выбор
    useEffect(() => {
        actions.insertCurrent(appendCurrent);
        // Только при изменении опций и открытия-закрытия
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, isOpen]);

    // Определить расположение дропдауна
    useLayoutEffect(() => {
        const setPosition = () => setCurrentPosition(getDropdownPosition(refs));
        const observer = new IntersectionObserver(
            ([{ intersectionRatio }]) => intersectionRatio < 1 && setPosition(),
            {
                threshold: [1],
            }
        );

        if (isOpen && autocompleteRef.current) {
            setPosition();
            observer.observe(autocompleteRef.current);
        }

        return () => {
            // eslint-disable-next-line no-unused-expressions
            observer.disconnect?.();
        };
    }, [isOpen]);

    const statusIconHidden = hideStatusIcon ?? !onChange;

    return (
        <div className={cn(styles.wrapper, className)}>
            <Input
                {...rest}
                addHiddenInput={addHiddenInput}
                autoComplete={autoComplete}
                caption={caption}
                className={cn(styles.external, styles.marginBottom0, styles.autofill)}
                dataTestId={dataTestId}
                disabled={disabled}
                error={error}
                forwardedRef={inputRef}
                hideStatusIcon={statusIconHidden}
                name={name}
                onBlur={actions.onBlurHandler}
                onCancel={onCancel}
                onChange={actions.onChangeHandler}
                onClick={actions.onClickHandler}
                onFocus={actions.onFocusHandler}
                onKeyDown={actions.onKeyDown}
                placeholder={placeholder}
                readOnly={readOnly}
                success={success}
                value={displayValue}
            />

            {arrow && (
                <ArrowIcon
                    // TODO: если для Select потребуется пропс dataTestId, то переписать на dataTestId={`${dataTestId}-arrow-icon`}
                    dataTestId="base-select-arrow-icon"
                    isOpen={isOpen}
                    onClick={actions.onDropdownToggle}
                />
            )}

            {isOpen && (
                <Autocomplete
                    autocompleteRef={autocompleteRef}
                    dataTestId={dataTestId}
                    highlightedItem={currentItem}
                    onSelect={actions.onDropDownSelect}
                    options={options}
                    position={currentPosition}
                    styles={styles}
                />
            )}
        </div>
    );
};
